<template>
  <master-layout>
    <div class="flex min-h-full flex-col bg-brown-50">
      <!-- The whole header sticks, not just the tabs: ion-content scrolls under
           the status bar, so a bar pinned at top-0 on its own collides with the
           clock and the Dynamic Island. Pinning the block that already starts
           below them is the pattern church-updates uses. -->
      <div class="sticky top-0 z-10 bg-brown-50">
        <BackButton class="w-full px-[20px] mb-[16px]" :customBack="goBack" />

        <!--
          28px matches the Courses landing page, and reads level with My Payments
          next door (30px, hardcoded there). `text-display` — what this line used
          to carry — matches no key in tailwind.config.js, so the title had been
          rendering at inherited body size all along.

          The filter sits on the same line: it belongs to the whole list, and the
          sentence that used to be here only restated the title.
        -->
        <div class="flex flex-row flex-wrap items-center justify-between gap-3 px-5 pb-4">
          <h1
            class="text-text-display-md min-w-0 break-normal font-black tracking-tight text-grey-100"
          >
            My Vouchers
          </h1>

          <div class="ms-auto flex flex-shrink-0 flex-row items-center gap-2">
            <!--
              The no-camera way onto the IDR unlock lane, and the reason it is on
              this page: `coursesGuard` bounces a V/N with no grant off every
              /courses/:programSlug route, so a code field inside Next Steps is
              unreachable by the members an IDR card is usually for. This wallet
              is where the book-collection voucher the same redeem issues lands,
              so a member holding a printed card is already on the right screen.

              In the header rather than in the list, so it is there in every
              state — an empty wallet is the likeliest state for someone who just
              bought a book and has redeemed nothing yet. Same height, radius and
              chrome as the filter beside it: the two read as one control pair.

              Gated on the SAME flag as `/courses/redeem/:code`, not on the
              wallet's own: the sheet's submit reaches the IDR-gated redemption,
              so while that lane is dormant this button would open a form the
              server answers with a 404. The door and the room share one switch.

              "Enter code", not "Redeem": on a screen listing vouchers tagged
              Available, "Redeem" reads as "spend one of these", and this spends
              nothing — it adds access. The verb has to name what happens next.
            -->
            <button
              v-if="isIdrUnlockEnabled"
              type="button"
              class="flex h-11 flex-shrink-0 items-center justify-center rounded-[14px] border border-grey-20 bg-white px-4 text-text-body-small font-bold text-grey-100 transition-transform duration-100 active:scale-[0.96] motion-reduce:transition-none"
              aria-label="Enter a code"
              @click="isCodeSheetOpen = true"
            >
              Enter code
            </button>

            <!--
              Shaped after the Courses notes hub, which is the app's most recent
              filter control: a square button at the standard hit size, a funnel,
              and a count badge when the filter is narrowing anything. The app has
              three different filter triggers today — a purple pill with a label in
              the attendance board, this square-with-badge in Courses, and nothing
              at all in Calendar, which drives its modal from Vuex. Following the
              newest rather than inventing a fourth.
            -->
            <button
              type="button"
              class="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] border border-grey-20 bg-white text-grey-100 transition-transform duration-100 active:scale-[0.96] motion-reduce:transition-none"
              :aria-label="filterLabel"
              @click="isFilterOpen = true"
            >
              <DsIcon name="filter" size="md" />
              <span
                v-if="hiddenKindCount"
                class="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-black text-grey-800"
              >
                {{ hiddenKindCount }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="flex flex-1 items-center justify-center pb-16">
        <Spinner size="lg" label="Loading vouchers" class="text-grey-60" />
      </div>

      <!-- Load failure with nothing cached: show a retry, never mislead an
           offline member into thinking their wallet is empty. If we already have
           vouchers in the store (re-entered offline), keep showing them rather
           than replacing a usable list with a hard error. -->
      <div
        v-else-if="loadError && !vouchers.length"
        class="flex flex-1 flex-col items-center justify-center px-8 pb-16 text-center"
      >
        <DsCard padding="lg" class="flex w-full flex-col items-center">
          <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brown-100">
            <DsIcon name="warning" size="xl" class="text-grey-100" />
          </div>
          <div class="text-body-1 font-bold text-grey-100">Couldn't load your vouchers</div>
          <div class="mt-2 text-body-small text-grey-60">{{ loadError }}</div>
          <DsButton class="mt-4 w-full" variant="secondary" size="48" shape="pill" @click="reload">
            Try again
          </DsButton>
        </DsCard>
      </div>

      <!-- pb-16 lifts the line above the section's true centre. The area runs
           from the filters to the bottom of the screen with no tab bar to
           shorten it, and a single line centred in a void that tall reads as
           sitting low — the eye weights the content above it. The loading and
           load-failure blocks above carry the same value, so all three states
           settle on the same line rather than stepping down the screen. -->
      <div
        v-else-if="!visibleVouchers.length"
        class="flex flex-1 flex-col items-center justify-center px-8 pb-16 text-center"
      >
        <div class="text-text-body-small text-grey-60">{{ emptyMessage }}</div>
        <DsButton
          v-if="hiddenCount"
          class="mt-3"
          variant="secondary"
          size="48"
          shape="pill"
          @click="isFilterOpen = true"
        >
          Change filter
        </DsButton>
      </div>

      <!-- Category is a section heading rather than a filter row: a member with
           three kinds of voucher can see all three at once instead of pivoting
           between them, and nothing is hidden behind a pill they didn't press.
           The heading is suppressed when there is only one group, where it would
           label the obvious. -->
      <div v-else class="flex flex-col px-5 mt-4 pb-8">
        <section
          v-for="group in groupedVouchers"
          :key="group.id"
          class="flex flex-col gap-3 mt-6 first:mt-0"
        >
          <h2 class="text-text-caption-md font-semibold uppercase tracking-wide text-grey-60">
            {{ group.name }}
          </h2>
          <VoucherCard
            v-for="voucher in group.vouchers"
            :key="voucher.id"
            :voucher="voucher"
            @click="openVoucher(voucher.id)"
          />
        </section>

        <!-- History is one flat section, not grouped by area: grouping what a
             member can no longer use adds structure to answer a question nobody
             asked. -->
        <section v-if="filters.spent && spentVouchers.length" class="mt-8 flex flex-col gap-3">
          <h2 class="text-text-caption-md font-semibold uppercase tracking-wide text-grey-60">
            Collected &amp; expired
          </h2>
          <VoucherCard
            v-for="voucher in spentVouchers"
            :key="voucher.id"
            :voucher="voucher"
            @click="openVoucher(voucher.id)"
          />
        </section>
      </div>

      <!-- Always present when the filter is holding something back, so nothing
           is ever missing without the screen saying so. -->
      <button
        v-if="hiddenCount"
        type="button"
        class="text-text-body-small mx-5 mb-8 flex min-h-[44px] flex-row items-center justify-center gap-1 text-grey-60"
        @click="isFilterOpen = true"
      >
        {{ hiddenCount }} more hidden by the filter
        <span class="font-semibold text-grey-100">Change</span>
      </button>

      <VoucherFilterSheet
        v-model="isFilterOpen"
        :selected="filters"
        :counts="filterCounts"
        @update:selected="filters = $event"
      />

      <Toast safe-area />
    </div>

    <HaveACodeSheet v-model="isCodeSheetOpen" @redeemed="onRedeemed" @needs-cg="onNeedsCgLink" />

    <!-- Only ever shown mid-redemption, when the server refuses to grant until
         this member has a main CG. The entry gate (needsCgHardBlock) is NOT
         bound: it guards Next Steps content, and a wallet is not that. -->
    <CgHardBlockModal :model-value="cgRedeemPending" @linked="onCgLinked" @exit="onCgBlockExit" />
  </master-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import {
  Button as DsButton,
  Card as DsCard,
  Icon as DsIcon,
  Spinner,
} from '@team-hogc/hogc-one-design-system';
import BackButton from '@/components/common/BackButton.vue';
import Toast from '@/components/common/Toast.vue';
import VoucherCard from '@/components/Vouchers/VoucherCard.vue';
import VoucherFilterSheet, {
  DEFAULT_VOUCHER_FILTER,
} from '@/components/Vouchers/VoucherFilterSheet.vue';
import { isExpired } from '@/utils/voucher-expiry';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { IDR_UNLOCK_FLAG } from '@/constants/courses';
import HaveACodeSheet from '@/pages/courses/components/HaveACodeSheet.vue';
import CgHardBlockModal from '@/pages/courses/components/CgHardBlockModal.vue';
import cgHardBlock from '@/mixins/CgHardBlock';

export default {
  mixins: [cgHardBlock],
  components: {
    BackButton,
    Toast,
    VoucherCard,
    VoucherFilterSheet,
    HaveACodeSheet,
    CgHardBlockModal,
    DsButton,
    DsCard,
    DsIcon,
    Spinner,
  },
  data() {
    return {
      // Opens already narrowed: history is hidden, everything usable is not.
      filters: { ...DEFAULT_VOUCHER_FILTER },
      isFilterOpen: false,
      isCodeSheetOpen: false,
      // Tracks a vouchers-load failure locally so a network error renders a
      // retry state instead of the "no vouchers" empty state. Kept separate
      // from the shared vouchers/error so an (optional) categories failure can
      // never masquerade as a wallet-load failure.
      loadError: null,
    };
  },
  computed: {
    ...mapState('vouchers', ['vouchers', 'categories']),
    ...mapGetters('vouchers', [
      'availableVouchers',
      'expiringVouchers',
      'redeemedVouchers',
      'unavailableVouchers',
      'expiredVouchers',
      'isLoading',
    ]),
    // Inside the computed, not at module scope: a module-scope useFeatureFlags()
    // runs at import time and trips specs that mock the composable with a hoisted
    // jest.mock. Same pattern as leaderDashboardEnabled in pages/courses/index.vue.
    isIdrUnlockEnabled() {
      const { isEnabled } = useFeatureFlags();
      return isEnabled(IDR_UNLOCK_FLAG);
    },
    /**
     * Everything still worth acting on, urgent first: what lapses soonest, then
     * the rest, then a pass the partner cancelled — kept in the main list on
     * purpose, because it is the member's book and the partner may restore it.
     */
    /** Each filter row, and what it stands for. One place decides both. */
    buckets() {
      return {
        available: this.availableVouchers,
        expiring: this.expiringVouchers.filter(v => !isExpired(v)),
        unavailable: this.unavailableVouchers,
        // Three ways a voucher lands in history: used, expired by a date we
        // hold, or expired by IDR — which arrives as a status because a
        // collection pass carries no date of its own.
        spent: [
          ...this.redeemedVouchers,
          ...this.expiredVouchers,
          ...this.expiringVouchers.filter(isExpired),
        ],
      };
    },
    filterCounts() {
      return Object.fromEntries(Object.entries(this.buckets).map(([k, v]) => [k, v.length]));
    },
    /** Still usable, urgent first — everything except history. */
    liveVouchers() {
      const { expiring, available, unavailable } = this.buckets;
      return [
        ...(this.filters.expiring ? expiring : []),
        ...(this.filters.available ? available : []),
        ...(this.filters.unavailable ? unavailable : []),
      ];
    },
    spentVouchers() {
      return this.buckets.spent;
    },
    visibleVouchers() {
      return this.filters.spent ? [...this.liveVouchers, ...this.spentVouchers] : this.liveVouchers;
    },
    /** What the filter is holding back right now, whatever the reason. */
    hiddenCount() {
      return Object.entries(this.buckets).reduce(
        (total, [key, list]) => total + (this.filters[key] ? 0 : list.length),
        0,
      );
    },
    /**
     * How many kinds the filter is holding back — what the badge counts.
     *
     * A kind only counts if it has something IN it. Counting switched-off
     * toggles instead meant the badge read "1" on an empty wallet, because
     * `spent` is off by default: the control announced that the filter was
     * narrowing something at the exact moment there was nothing to narrow, and
     * would have gone on saying it for every member who has never spent a
     * voucher. `hiddenCount` below already had this right, which is why the
     * "N more hidden" line correctly stayed away.
     */
    hiddenKindCount() {
      return Object.entries(this.buckets).filter(
        ([key, list]) => !this.filters[key] && list.length > 0,
      ).length;
    },
    /**
     * An empty list has two very different causes, and saying the wrong one
     * sends somebody looking for a voucher that was never missing.
     */
    emptyMessage() {
      if (this.hiddenCount) return 'Nothing matches the filter.';
      return 'Nothing here yet.';
    },
    filterLabel() {
      const chosen = Object.keys(this.buckets).filter(k => this.filters[k]).length;
      const all = Object.keys(this.buckets).length;
      return chosen === all
        ? 'Filter vouchers — showing everything'
        : `Filter vouchers — showing ${chosen} of ${all} kinds`;
    },
    /**
     * The visible vouchers grouped by category, in the order the category list
     * gives — anything whose category is missing from that list sorts last
     * under its own name rather than disappearing.
     */
    groupedVouchers() {
      const order = this.categories.map(c => c.id);
      const rank = id => {
        const i = order.indexOf(id);
        return i === -1 ? order.length : i;
      };
      const groups = new Map();
      this.liveVouchers.forEach(voucher => {
        const id = voucher.categoryId || 'uncategorised';
        if (!groups.has(id)) {
          groups.set(id, { id, name: voucher.categoryName || 'Other', vouchers: [] });
        }
        groups.get(id).vouchers.push(voucher);
      });
      return [...groups.values()].sort((a, b) => rank(a.id) - rank(b.id));
    },
  },
  async ionViewWillEnter() {
    await this.reload();
  },
  methods: {
    async reload() {
      this.loadError = null;
      // Concurrent: the two share no data, and awaiting them in turn put a whole
      // round trip of latency in front of the wallet painting. Each keeps its own
      // catch, because they fail differently — one blocks the screen, the other
      // only costs the category filters.
      await Promise.all([
        this.$store.dispatch('vouchers/fetchVouchers').catch(error => {
          // The store has already toasted; capture the message so the page can
          // show a retry state (and swallow the rejection so it isn't unhandled).
          this.loadError = error?.response?.data?.message || 'Check your connection and try again.';
        }),
        // Categories are reference data and rarely change, so only fetch them
        // once per session rather than on every back-navigation from a voucher.
        this.categories.length
          ? Promise.resolve()
          : this.$store.dispatch('vouchers/fetchCategories').catch(() => {
              // Category filters are optional; the wallet still renders vouchers.
            }),
      ]);
    },
    goBack() {
      this.$router.push('/tabs/me');
    },
    openVoucher(id) {
      this.$router.push(`/vouchers/${id}`);
    },
    // A redeem always lands on the program it unlocked, never back on the
    // wallet: the grant is what gets this member past coursesGuard, and leaving
    // them here would make them work out what just happened. The book-collection
    // voucher the same redeem issues is waiting when they come back.
    onRedeemed(programSlug) {
      if (programSlug) this.$router.push(`/courses/${programSlug}`);
    },
  },
};
</script>
